import { useEffect, useRef, useState } from 'react';

export const NOVO_ID = 'novo';

export const getPages = (page, totalPages) => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 3) return [1, 2, 3, '...', totalPages];
    if (page >= totalPages - 2) return [1, '...', totalPages - 2, totalPages - 1, totalPages];
    return [1, '...', page - 1, page, page + 1, '...', totalPages];
};

/**
 * `ordemPadrao` (default true) mantém a ordem em que `itens` chega da API sem
 * reordenar; `false` apenas inverte esse array. O hook não sabe (nem precisa
 * saber) se a API ordena por data crescente, decrescente ou por campo `Ordem` —
 * cada consumidor deve mapear `ordemPadrao` para os rótulos do próprio select
 * de acordo com a ordenação real que seu endpoint retorna (ver TabelaPlanoAcao
 * vs. TabelaDiarioOperacao, que usam o mesmo booleano com rótulos invertidos
 * porque suas APIs ordenam em direções opostas).
 *
 * Com `paginacaoServidor`, busca, ordenação e paginação ficam a cargo da API:
 * `itens` é exibido como chegou e o consumidor usa `busca`/`ordemPadrao` para
 * montar a requisição.
 */
export default function useTabelaCrudInline({
    itens,
    linhaVazia,
    valoresParaEdicao,
    montarPayload = (valores) => valores,
    filtrar,
    nomeParaExclusao,
    onCriar,
    onEditar,
    onExcluir,
    itensPorPagina = 10,
    paginacaoServidor = false,
}) {
    const [linhaEmEdicaoId, setLinhaEmEdicaoId] = useState(null);
    const [valores, setValores] = useState(linhaVazia());
    const [itemRemovido, setItemRemovido] = useState();
    const [idRemover, setIdRemover] = useState();
    const [paginaAtual, setPaginaAtual] = useState(1);
    const [buscaInput, setBuscaInput] = useState('');
    const [busca, setBusca] = useState('');
    const [ordemPadrao, setOrdemPadrao] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const salvandoRef = useRef(false);
    const excluindoRef = useRef(false);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setBusca(buscaInput);
            setPaginaAtual(1);
        }, 400);

        return () => clearTimeout(timeout);
    }, [buscaInput]);

    const itensFiltrados = paginacaoServidor || busca.trim() === ''
        ? itens
        : itens.filter((item) => filtrar(item, busca.trim().toLowerCase()));

    const itensOrdenados = paginacaoServidor || ordemPadrao ? itensFiltrados : [...itensFiltrados].reverse();

    const totalPaginas = Math.max(1, Math.ceil(itensOrdenados.length / itensPorPagina));

    useEffect(() => {
        if (paginaAtual > totalPaginas) setPaginaAtual(totalPaginas);
    }, [totalPaginas, paginaAtual]);

    const itensPaginados = paginacaoServidor ? itensOrdenados : itensOrdenados.slice(
        (paginaAtual - 1) * itensPorPagina,
        paginaAtual * itensPorPagina
    );

    const alterarOrdem = (valor) => {
        setOrdemPadrao(valor);
        setPaginaAtual(1);
    };

    const iniciarEdicao = (item) => {
        setLinhaEmEdicaoId(item.id);
        setValores(valoresParaEdicao(item));
    };

    const iniciarNovo = () => {
        setLinhaEmEdicaoId(NOVO_ID);
        setValores(linhaVazia());
    };

    const cancelar = () => {
        setLinhaEmEdicaoId(null);
        setValores(linhaVazia());
    };

    const salvar = async () => {
        if (salvandoRef.current) return;
        salvandoRef.current = true;
        setSalvando(true);

        try {
            const payload = montarPayload(valores);

            const sucesso = linhaEmEdicaoId === NOVO_ID
                ? await onCriar(payload)
                : await onEditar(linhaEmEdicaoId, payload);

            if (!sucesso) return;

            setLinhaEmEdicaoId(null);
            setValores(linhaVazia());
        } finally {
            salvandoRef.current = false;
            setSalvando(false);
        }
    };

    const selecionarParaExcluir = (item) => {
        setItemRemovido(nomeParaExclusao(item));
        setIdRemover(item.id);
    };

    const confirmarExclusao = async () => {
        if (excluindoRef.current) return;
        excluindoRef.current = true;

        try {
            await onExcluir(idRemover);
        } finally {
            excluindoRef.current = false;
        }
    };

    return {
        NOVO_ID,
        linhaEmEdicaoId,
        valores,
        onChangeValor: (campo, valor) => setValores((v) => ({ ...v, [campo]: valor })),
        itemRemovido,
        paginaAtual,
        setPaginaAtual,
        buscaInput,
        setBuscaInput,
        busca,
        ordemPadrao,
        alterarOrdem,
        salvando,
        itensOrdenados,
        itensPaginados,
        totalPaginas,
        iniciarEdicao,
        iniciarNovo,
        cancelar,
        salvar,
        selecionarParaExcluir,
        confirmarExclusao,
    };
}
